import type {
	ConstantsPublicTeamInviteResponse,
	ConstantsPublicTeamMemberResponse,
	ConstantsTeamResponse,
} from "@gosaas/types";
import { hasPermission, type Permission, type Role } from "@gosaas/types/rbac";
import { IconUserPlus, IconUsers } from "@tabler/icons-react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { InviteTeamMemberDialog } from "@/components/dialogs/add-team-member";
import { Loader } from "@/components/Loader";
import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { useTeam } from "@/components/providers/team";
import {
	useFetchTeamInvites,
	useFetchTeamMembers,
} from "@/components/queries/team";
import {
	teamInviteColumns,
	teamMemberColumns,
} from "@/components/tables/columns";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";

export function shouldRedirectPersonalTeamMembers(
	team?: Pick<ConstantsTeamResponse, "type">,
): boolean {
	return team?.type === "personal";
}

export function canShowInviteMemberButton(
	teamType: string | undefined,
	canAddMember: boolean,
): boolean {
	return teamType !== "personal" && canAddMember;
}

export function Members() {
	const { teamID, teams } = useTeam();
	const [open, setOpen] = useState(false);
	const router = useRouter();
	const [denied, setDenied] = useState(false);

	const team = teams.find((t) => t.id === teamID);

	const canReadMembers = hasPermission(
		team?.role as Role,
		"members:read" as Permission,
	);

	const { teamMembers, isLoading } = useFetchTeamMembers(teamID ?? "");
	const { teamInvites, isLoadingInvites, refetchInvites } = useFetchTeamInvites(
		teamID ?? "",
	);

	useEffect(() => {
		if (team && shouldRedirectPersonalTeamMembers(team)) {
			router.navigate({ to: "/team/settings" });
			return;
		}
		if (!canReadMembers) {
			toast.warning("You do not have permission to manage members", {
				description: "Please contact support if you believe this is an error.",
			});
			router.navigate({ to: "/403" });
			setDenied(true);
		}
	}, [router, team, canReadMembers]);

	if (!teamID) {
		return <Loader />;
	}

	if (denied) {
		return null;
	}

	if (isLoading || isLoadingInvites) {
		return <Loader />;
	}

	const canAddMember = hasPermission(
		team?.role as Role,
		"members:create" as Permission,
	);

	return (
		<PageLayout>
			<PageHeader
				breadcrumbItems={[
					{ title: "Team", href: null },
					{ title: "Members", href: "/team/members" },
				]}
				title="Team Members"
				description="Manage your team members and pending invites"
				icon={<IconUsers className="size-6 text-primary" aria-hidden />}
			/>

			<Card className="shadow-sm">
				<CardHeader className="pb-4">
					<div className="flex items-start sm:items-center justify-between gap-3">
						<div className="flex items-center gap-3">
							<div className="p-2 bg-blue-500/10 rounded-lg">
								<IconUsers className="h-5 w-5 text-blue-500" />
							</div>
							<div>
								<CardTitle className="text-xl">Members</CardTitle>
								<p className="text-sm text-muted-foreground mt-1">
									View, filter, and manage all members of your team
								</p>
							</div>
						</div>
						{canShowInviteMemberButton(team?.type, canAddMember) && (
							<Button
								onClick={() => setOpen(true)}
								className="whitespace-nowrap"
							>
								<IconUserPlus className="h-4 w-4 mr-2" />
								Invite member
							</Button>
						)}
					</div>
					<InviteTeamMemberDialog
						open={open}
						onOpenChange={setOpen}
						onInviteSuccess={() => {
							refetchInvites();
						}}
					/>
				</CardHeader>
				<CardContent className="pt-0">
					{canReadMembers && (
						<DataTable
							columns={teamMemberColumns}
							data={teamMembers as ConstantsPublicTeamMemberResponse[]}
							loading={isLoading}
							filterColumn="role"
							usePagination={true}
							pageSize={5}
						/>
					)}
				</CardContent>
			</Card>

			<Card className="shadow-sm">
				<CardHeader className="pb-4">
					<div className="flex items-center gap-3">
						<div className="p-2 bg-green-500/10 rounded-lg">
							<IconUserPlus className="h-5 w-5 text-green-600" />
						</div>
						<div>
							<CardTitle className="text-xl">Pending Invites</CardTitle>
							<CardDescription>
								Track outstanding invitations and their statuses
							</CardDescription>
						</div>
					</div>
				</CardHeader>
				<CardContent className="pt-0">
					<DataTable
						columns={teamInviteColumns}
						data={teamInvites as ConstantsPublicTeamInviteResponse[]}
						loading={isLoadingInvites}
						filterColumn="email"
						usePagination={true}
						pageSize={5}
					/>
				</CardContent>
			</Card>
		</PageLayout>
	);
}

/* istanbul ignore start -- TanStack Router file route registration */
export const Route = createFileRoute("/_authorized/team/members")({
	component: Members,
});
/* istanbul ignore end */
